import Icon from '@ant-design/icons';
import { ReactComponent as referralIcon } from '@/assets/icons/referral-url.svg';
import { ReactComponent as launchIcon } from '@/assets/icons/launch.svg';
import { ReactComponent as paymentIcon } from '@/assets/icons/payment-card.svg';
import { User } from '@/reducers/product-management';
import tracker from '@/tracker';
import { GeoLocation } from '@/api/geo-location';
import { useQuery } from '@tanstack/react-query';
import { callGetApi, callPostApi } from '@/components/api-common';
import PulseRipple from '@/common/components/PulseRipple';
import { useCampaignPulse } from './useCampaignPulseVisible';
import { useEffect } from 'react';

const AFFILIATE_LINK = 'https://affiliate.arcsite.com/';
const BOOK_DEMO_LINK = 'https://www.arcsite.com/book-demo-inapp';
const COLLECT_PAYMENT_LINK = 'http://arcsite.com/payments-signup';

const MATCHED_USE_CASES = [
  'Capture Site Data',
  'Perform Material Takeoffs',
  'Build Estimates and Proposals',
];
const MATCHED_INDUSTRY = [
  'Basement Waterproofing',
  'Building Inspection',
  'Decks & Patios',
  'Fencing',
  'Fire Inspection',
  'Foundation Repair',
  'Gutters',
  'Landscape & Irrigation',
  'Pest Control',
  'Site Audits / Inspection',
  'Turf / Synthetic Turf',
  'Architecture',
  'Architectural Engineering',
  'Building Controls',
  'Civil Engineering',
  'Concrete',
  'Electrical / Lighting',
  'Electrical Engineering',
  'Energy & Solar',
  'Engineering & Mechanical',
  'Flooring',
  'HVAC',
  'Landscape Designer',
  'Maintenance',
  'Mechanical/Mechanical Engineering',
  'Plumbing',
  'Roofing',
  'Security / Security Inspection',
  'Structural Engineering',
  'Sub Contractor',
  'Telecom',
];

export const USER_CAMPAIGN_MAP = {
  BookDemo: {
    key: 'BookDemo',
    text: 'Book Demo',
    link: BOOK_DEMO_LINK,
    icon: launchIcon,
  },
  BecomeAnAffiliate: {
    key: 'BecomeAnAffiliate',
    text: 'Become an Affiliate',
    link: AFFILIATE_LINK,
    icon: referralIcon,
  },
  CollectPayment: {
    key: 'CollectPayment',
    text: 'Collect Payments',
    link: COLLECT_PAYMENT_LINK,
    icon: paymentIcon,
  },
} as const;

export default function ArcSiteCampaign({ user }: { user: User }) {
  const campaign = useArcSiteCampaign(user);
  const { shouldShowPulse, handleVisit, handleCampaignClick } =
    useCampaignPulse();
  useEffect(() => {
    if (campaign?.key === 'CollectPayment') {
      handleVisit();
    }
  }, [campaign?.key, handleVisit]);
  if (!campaign) return null;
  const { key, text, icon, link } = campaign;
  return (
    <a
      className="relative mr-4 hidden sm:block"
      href={link}
      target="_blank"
      rel="noreferrer"
      onClick={() => {
        if (key === 'BookDemo') {
          tracker.logEvent('web_book_demo_button_click');
        } else if (key === 'CollectPayment') {
          handleCampaignClick();
          tracker.logEvent('tap_request_arcsite_payments', {
            Source: 'web',
          });
        } else {
          tracker.logEvent('web_become_an_affiliate_button_click');
        }
      }}
    >
      <div className="bg-[#E3571C] text-xs px-2.5 py-2 text-white rounded font-semibold flex items-center">
        <Icon component={icon} className="text-16" />
        <span className="whitespace-nowrap ml-2 hidden sm:block">{text}</span>
      </div>
      {key === 'CollectPayment' && shouldShowPulse && (
        <PulseRipple className="absolute -top-2 -right-2" />
      )}
    </a>
  );
}

const useArcSiteCampaign = (user: User) => {
  const bookDemoTierMatched =
    user.default_org_purchase_type == 'TRIAL' ||
    user.default_org_user_level === 'BASIC' ||
    user.default_org_user_level === 'PRO';

  const collectPaymentTierMatched =
    user.default_org_user_level == 'PREMIUM_PLUS' &&
    !user.expired &&
    user.default_org_purchase_type !== 'TRIAL';

  const { data: geoLocation, isLoading: isGeoLocationLoading } = useQuery({
    queryKey: ['geo-location'],
    queryFn: () => callGetApi<GeoLocation>('user/service/geo_location/'),
    enabled: collectPaymentTierMatched,
  });
  const { data: paymentSettings, isLoading: isPaymentSettingsLoading } =
    useQuery({
      queryKey: ['payment_settings'],
      queryFn: () =>
        callPostApi<{ enabled: boolean }>(
          '/user/service/payments/payment_settings/'
        ),
      enabled: collectPaymentTierMatched,
    });
  const isUS = geoLocation?.country_code === 'US';
  const isPaymentEnabled = paymentSettings?.enabled;

  const useCaseMatched = MATCHED_USE_CASES.some(useCase =>
    user.use_cases.includes(useCase)
  );
  const industryMatched = MATCHED_INDUSTRY.includes(user.industry || '');

  if (bookDemoTierMatched && (useCaseMatched || industryMatched)) {
    return USER_CAMPAIGN_MAP.BookDemo;
  }

  if (isGeoLocationLoading || isPaymentSettingsLoading) {
    return null;
  }

  if (collectPaymentTierMatched && isUS && !isPaymentEnabled) {
    return USER_CAMPAIGN_MAP.CollectPayment;
  }

  return USER_CAMPAIGN_MAP.BecomeAnAffiliate;
};
